/**
 * Global Interval Manager
 * Uses recursive setTimeout instead of setInterval to prevent:
 * - Overlapping executions when callbacks take longer than the delay
 * - Queued ticks firing all at once after browser suspension
 * Includes suspension recovery via visibility/focus events.
 */

interface IntervalOptions {
  maxRetries?: number;
  retryDelay?: number;
  onError?: (error: Error, attempt: number) => void;
  onRestart?: () => void;
  /** If true, enables suspension recovery when the platform suspends JS timers */
  enableDriftDetection?: boolean;
  /** Multiplier of delay after which the interval is considered suspended (default: 3) */
  driftThreshold?: number;
}

interface IntervalEntry {
  timeoutId: number;
  callback: Function;
  delay: number;
  options: IntervalOptions;
  retryAttempts: number;
  lastTickTime: number;
  isRunning: boolean;
}

class GlobalIntervalManager {
  private entries: Map<string, IntervalEntry> = new Map();
  private eventListenersAttached: boolean = false;
  private resumeCheckDebounceId: number | null = null;

  /**
   * Creates a safe recurring timer using recursive setTimeout
   * @param id - Unique identifier for the interval
   * @param callback - Function to execute (supports async)
   * @param delay - Delay in milliseconds between end of one execution and start of next
   * @param options - Additional options
   */
  public createSafeInterval(
    id: string,
    callback: Function,
    delay: number,
    options: IntervalOptions = {},
  ): void {
    // If an interval with this id already exists, stop it first
    if (this.entries.has(id)) {
      this.stopInterval(id);
    }

    const entry: IntervalEntry = {
      timeoutId: 0,
      callback: callback,
      delay: delay,
      options: options,
      retryAttempts: 0,
      lastTickTime: Date.now(),
      isRunning: true,
    };

    this.entries.set(id, entry);
    this.scheduleNext(id, entry);

    if (options.enableDriftDetection !== false) {
      this.ensureSuspensionRecovery();
    }
  }

  /**
   * Schedules the next tick using setTimeout. After execution completes, schedules again.
   */
  private scheduleNext(id: string, entry: IntervalEntry): void {
    if (!entry.isRunning) return;

    entry.timeoutId = setTimeout(async () => {
      if (!entry.isRunning) return;

      try {
        await entry.callback();
        entry.retryAttempts = 0;
        entry.lastTickTime = Date.now();
      } catch (error) {
        entry.retryAttempts += 1;
        entry.lastTickTime = Date.now();

        const maxRetries = entry.options.maxRetries || 3;

        if (entry.options.onError) {
          try {
            entry.options.onError(error as Error, entry.retryAttempts);
          } catch (handlerError) {}
        }

        if (entry.retryAttempts >= maxRetries) {
          this.restartInterval(id);
          return;
        }
      }

      // Schedule next tick only after current one completes
      this.scheduleNext(id, entry);
    }, entry.delay) as unknown as number;
  }

  /**
   * Restarts an interval after repeated failures or suspension recovery
   */
  private restartInterval(id: string): void {
    const entry = this.entries.get(id);
    if (!entry || !entry.isRunning) return;

    const safeEntry = entry;

    // Clear the pending timeout
    clearTimeout(safeEntry.timeoutId);

    // Reset retry attempts
    safeEntry.retryAttempts = 0;

    const retryDelay = safeEntry.options.retryDelay || 1000;

    // Call restart handler if provided
    if (safeEntry.options.onRestart) {
      try {
        safeEntry.options.onRestart();
      } catch (error) {}
    }

    // Restart after a delay
    const self = this;
    setTimeout(function () {
      if (safeEntry.isRunning) {
        safeEntry.lastTickTime = Date.now();
        self.scheduleNext(id, safeEntry);
      }
    }, retryDelay);
  }

  /**
   * Attaches event listeners that fire when the app resumes from suspension.
   * On resume, checks if any interval has been suspended too long and restarts it.
   */
  private ensureSuspensionRecovery(): void {
    if (this.eventListenersAttached) return;
    this.eventListenersAttached = true;

    const self = this;

    const onResume = function () {
      if (self.resumeCheckDebounceId !== null) {
        clearTimeout(self.resumeCheckDebounceId);
      }
      self.resumeCheckDebounceId = setTimeout(function () {
        self.resumeCheckDebounceId = null;
        self.restartSuspendedIntervals();
      }, 3000) as unknown as number;
    };

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        onResume();
      }
    });

    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);

    /* For webOS and other platforms that emit a 'resume' event.
    Only available for chromium 68+ but the most accurate signal for our usecase */
    document.addEventListener('resume', onResume);
  }

  /**
   * Restarts any interval whose last tick exceeds the allowed gap (delay * threshold).
   */
  private restartSuspendedIntervals(): void {
    const now = Date.now();
    this.entries.forEach((entry: IntervalEntry, id: string) => {
      if (!entry.isRunning) return;

      const threshold = entry.options.driftThreshold || 3;
      const maxAllowedGap = entry.delay * threshold;

      if (now - entry.lastTickTime > maxAllowedGap) {
        this.restartInterval(id);
      }
    });
  }

  /**
   * Stops a specific interval by id
   */
  public stopInterval(id: string): void {
    const entry = this.entries.get(id);
    if (entry) {
      clearTimeout(entry.timeoutId);
      entry.isRunning = false;
      this.entries.delete(id);
    }
  }

  /**
   * Stops all intervals
   */
  public stopAllIntervals(): void {
    this.entries.forEach(function (entry) {
      clearTimeout(entry.timeoutId);
      entry.isRunning = false;
    });
    this.entries.clear();
  }
}

// Create and export singleton instance
export const globalIntervalManager = new GlobalIntervalManager();
export default globalIntervalManager;
